// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useUpdateInvoice } from '@/billing/hooks/use-update-invoice';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { PopoverForm } from '@/components/popover-form';
import { useToast } from '@/contexts/toast-context';
import { useDisclosure } from '@/hooks/use-disclosure';
import { usePopoverAutoFocus } from '@/hooks/use-popover-auto-focus';
import { useTimeout } from '@/hooks/use-timeout';
import { Box, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { ReactNode, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';

interface InlineEditableFieldProps {
  value: string | null;
  invoiceKey: string;
  field: 'memo' | 'paymentInstructions' | 'notes' | 'purchaseOrderNumber';
  readOnly?: boolean;
  placeholder?: string;
  rows?: number;
  minHeight?: string;
  fillContainer?: boolean;
}

export function InlineEditableField(props: InlineEditableFieldProps) {
  const theme = useTheme();
  const editor = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = usePopoverAutoFocus(editor.isOpen);
  const { showToast } = useToast();

  const { formState, register, reset, handleSubmit } = useForm({
    defaultValues: { value: props.value ?? '' },
  });

  const mutation = useUpdateInvoice({
    invoiceKey: props.invoiceKey,
    onSuccess: () => {
      editor.close();
    },
    onError: (message) => {
      showToast({
        severity: 'error',
        element: <span>{message}</span>,
        duration: 5000,
      });
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const handleClose = useCallback(() => {
    editor.close();
    timeout.start();
  }, [editor, timeout]);

  const handleValidSubmit = useCallback(
    (data: { value: string }) => {
      if (formState.isDirty) {
        const newValue = data.value.trim() || null;
        mutation.mutate({
          invoiceKey: props.invoiceKey,
          [props.field]: newValue,
        });
        reset({ value: data.value });
        return;
      }
      editor.close();
    },
    [formState.isDirty, props.invoiceKey, props.field, mutation, reset, editor]
  );

  const displayText = props.value || props.placeholder || 'Not provided';
  const color = props.value ? 'text.primary' : 'text.secondary';

  if (props.readOnly) {
    return (
      <Tooltip title="Only editable in draft status" arrow>
        <Typography
          variant="body2"
          color={color}
          sx={{
            whiteSpace: 'pre-wrap',
            minHeight: props.minHeight ?? '3rem',
            borderLeft: '2px solid',
            borderColor: 'divider',
            pl: 1,
          }}
        >
          {displayText}
        </Typography>
      </Tooltip>
    );
  }

  const button = (
    <EditablePropertyButton
      ref={buttonRef}
      onClick={editor.open}
      sx={{
        width: '100%',
        minHeight: props.minHeight ?? '3rem',
        alignItems: 'start',
        justifyContent: 'start',
        whiteSpace: 'pre-wrap',
        textAlign: 'left',
        borderLeft: '2px solid',
        borderColor: 'divider',
        borderRadius: 0,
        position: 'relative',
        ...(props.fillContainer && {
          // When fillContainer is on, the wrapper Box supplies the flex
          // growth; the button just needs to occupy the full interior of
          // that wrapper. Setting height here (instead of alignSelf on
          // the button) avoids the styled Button's baked-in
          // `alignSelf: 'start'` winning the cascade — which previously
          // made the notes field render at its minHeight on some loads.
          height: '100%',
          display: 'flex',
        }),
        '& .edit-icon': {
          position: 'absolute',
          top: '0.4rem',
          right: '0.3rem',
          margin: 0,
        },
      }}
    >
      <Typography
        variant="body2"
        color={color}
        sx={{
          whiteSpace: 'pre-wrap',
          width: '100%',
          ...(props.fillContainer
            ? {
                // Fill the full button interior and scroll long notes
                // rather than clamping at a fixed line count — the
                // container height governs the visible area.
                flex: 1,
                overflow: 'auto',
              }
            : {
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }),
        }}
      >
        {displayText}
      </Typography>
    </EditablePropertyButton>
  );

  // When fillContainer is on, the button is wrapped in a Box that owns the
  // flex growth inside the parent column. This isolates the sizing contract
  // (flex: 1 + stretch) on an unstyled Box so the caller's parent flex
  // column always stretches the button region, regardless of whatever
  // self-alignment rules MUI's Button has baked in.
  const displayNode: ReactNode = props.fillContainer ? (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignSelf: 'stretch',
        width: '100%',
      }}
    >
      {button}
    </Box>
  ) : (
    button
  );

  return (
    <>
      {displayNode}
      <PopoverForm
        verticalOffset={24}
        horizontalOffset={8}
        position="left"
        verticalPosition="top"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmit)}
        width={buttonRef.current?.offsetWidth}
      >
        <TextField
          autoFocus
          inputRef={inputRef}
          multiline
          rows={props.rows ?? 3}
          size="small"
          fullWidth
          InputProps={register('value')}
        />
      </PopoverForm>
    </>
  );
}
